import { type ReactNode, type FC } from 'react'
import { 
    useTheme, 
    TableContainer, 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableRow, 
    TablePagination, 
    TableSortLabel,
    Checkbox,
    Skeleton,
    CircularProgress,
    darken, 
} from '@mui/material'

import { ActionMenu, type ActionMenuItem } from '../ActionMenu/ActionMenu'
import type { POrder } from '../../types/Types'

export type PTableColumn = { 
    name: string, 
    headerName: string,
    sortable: boolean,
    width?: number, 
    align?: 'right' | 'left' | 'center' 
}

export type PTableRow = { 
    index: string | number
    cells: React.ReactNode[]
    actions?: ActionMenuItem[]
    clickable?: boolean,
    selectable?: boolean
}

type PTableProps = { 
    columns: PTableColumn[], 
    rows: PTableRow[],
    page: number,
    totalNumberOfRows:number, 
    numberOfRowsPerPage:number,
    rowsPerPageOptions:number[],
    orderBy?: string,
    order?: POrder,
    loading?: boolean,
    showActions?: boolean,
    hideHeader?: boolean,
    hidePaging?: boolean,
    selectable?: boolean,
    selectedIndexes?: (string | number)[],
    rowActionInProgress?: (string | number)[],
    onSortChange?: (orderBy:string, order:POrder) => void,
    onPageChange: (page:number) => void,
    onRowsPerPageChange: (rowsPerPage:number) => void,
    onRowSelection?: (rowIndex:string | number) => void
    onRowClick?: (rowIndex:string | number) => void
}


export const PTable: FC<PTableProps> = ({
    columns, 
    rows,
    page,
    totalNumberOfRows, 
    numberOfRowsPerPage,
    rowsPerPageOptions,
    orderBy,
    order,
    loading,
    showActions,
    hideHeader,
    hidePaging,
    selectable,
    selectedIndexes,
    rowActionInProgress,
    onSortChange,
    onPageChange,
    onRowsPerPageChange,
    onRowSelection,
    onRowClick
}) => {

    const theme = useTheme()

    let tableHeader 

    if(!hideHeader && columns) {

        let tableHeaderCells:ReactNode[] = []

        if(selectable) {
            tableHeaderCells.push(<TableCell 
                sx={{
                    backgroundColor:darken(theme.palette.background.paper, 0.1),
                    width: '1%'
                }}
                key={-1}>
            </TableCell>
            )
        }

        columns.map( (item, key) => {

            let cellContent;

            const sortChangeHandle = () => {
                if(onSortChange) {
                    onSortChange(item.name, order==='asc'?'desc':'asc')
                }
            } 

            if(item.sortable) {
                cellContent = (
                    <TableSortLabel 
                        onClick={sortChangeHandle} 
                        active={ orderBy === item.name }
                        direction={ (orderBy === item.name)?order:undefined }
                    >
                        {item.headerName}
                    </TableSortLabel>
                )
            }
            else {
                cellContent = item.headerName
            }
           
            tableHeaderCells.push(
                <TableCell 
                    sx={{
                    backgroundColor:darken(theme.palette.background.paper, 0.1),
                        ...(item.width && {width: `${item.width}%`})
                    }}
                    key={key} 
                    align={item.align}>
                        {cellContent}
                </TableCell>
            )
        })

        tableHeader = (
            <TableHead> 
                <TableRow>
                    {tableHeaderCells}
                </TableRow>
            </TableHead>
        )
    }
    let tableRows = []

    if(loading) {

        for(let i = 0; i < numberOfRowsPerPage; i++) {
            tableRows.push(
                <TableRow key={i}>
                    <TableCell colSpan={columns.length + (selectable?1:0)} sx={{borderColor: theme.palette.divider}}>
                        <Skeleton variant='rounded' height={32}/>
                    </TableCell>
                </TableRow>
            )
        }
    }
    else {
        tableRows = rows.map( (row, rowKey) => {
            let cells:ReactNode[] = []

            if(selectable) {            
                cells.push(
                    <TableCell key={-1}>
                        <Checkbox
                            disabled={(row.selectable !== undefined) && !row.selectable}
                            color='secondary'
                            checked={selectedIndexes?.includes(row.index)}
                            onClick={(e) => { e.stopPropagation() }}
                            onChange={() => {
                                if(onRowSelection) {
                                    onRowSelection(row.index)
                                }
                            }}
                        />
                    </TableCell>
                )
            }
        
            row.cells.forEach( (cell, cellKey) => {
                cells.push(
                    <TableCell key={cellKey}>
                        {cell}
                    </TableCell>
                )
            })

            if(showActions) {
                if(row.actions && row.actions.length) {

                    cells.push(
                        <TableCell
                            align='right'
                            key={row.cells.length}>
                            {
                                (rowActionInProgress?.includes(row.index))?
                                <CircularProgress size='2rem' sx={{mr:0.5}}/>:
                                <ActionMenu menuItems={row.actions}/>
                            }
                        </TableCell>
                    )
                }
                else {
                    cells.push(
                        <TableCell key={row.cells.length}/>
                    )
                }
            }

            const clickableRow = (onRowClick && row.clickable && !selectable)

            return (
                <TableRow
                    key={rowKey}
                    style={{ cursor: ( clickableRow ? 'pointer' : 'default')}}
                    hover={!!(clickableRow)}
                    onClick={() => {
                        if(clickableRow) onRowClick!(row.index)
                    }}>
                    {cells}
                </TableRow>
            ) 
        })

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * numberOfRowsPerPage - totalNumberOfRows) : 0
        if( emptyRows > 0 ){
            tableRows.push(
                <TableRow sx={{ height: 66 * emptyRows }}>
                    <TableCell colSpan={columns.length}/>
                </TableRow>
            )
        }
    }

    return (
        <>
            <TableContainer>
                <Table stickyHeader>
                    {tableHeader}
                    <TableBody>
                        {tableRows}
                    </TableBody>
                </Table>
            </TableContainer>
            {
                !hidePaging &&
                <TablePagination
                    sx={{
                        overflow:'visible',
                        backgroundColor:darken(theme.palette.background.paper, 0.1),
                    }}
                    component='div'
                    page={page}
                    count={totalNumberOfRows}
                    rowsPerPage={numberOfRowsPerPage} 
                    rowsPerPageOptions={rowsPerPageOptions}
                    onRowsPerPageChange={ (event) => onRowsPerPageChange(parseInt(event.target.value, 10)) } 
                    onPageChange={ (_event, page) => onPageChange(page)}
                />
            }
        </>
    )
}